import React, { useState, useEffect, useRef } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Calendar } from "primereact/calendar";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { Badge } from "primereact/badge";
import "primereact/resources/themes/lara-light-blue/theme.css";

import { makeRequest2 } from "../../../../../utils/makeRequest";
import { toastify } from "../../../../../utils/toast";
import { useFacilityStore } from "../../../../../app/z-store/store";
import { getItem } from "../../../../../utils/localStorage";

const DailyChecklistTab = () => {
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [expandedRows, setExpandedRows] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  // Modal States
  const [showCreateParentDialog, setShowCreateParentDialog] = useState(false);
  const [showAddReadingDialog, setShowAddReadingDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);

  // Form States
  const [parentForm, setParentForm] = useState({
    parentName: "",
    checklistDate: new Date(),
  });

  const [readingForm, setReadingForm] = useState({
    parentName: "",
    childName: "",
    value: "",
    date: new Date(),
    unit: "",
    notes: "",
    recordedBy: "",
  });

  const [readingHistory, setReadingHistory] = useState([]);
  const [selectedChecklist, setSelectedChecklist] = useState(null);
  const [selectedParent, setSelectedParent] = useState(null);
  const [selectedChild, setSelectedChild] = useState(null);

  const toast = useRef(null);
  const selectedFacilityId = useFacilityStore((state) => state.facilityId);

  // Unit options for dropdown
  const unitOptions = [
    { label: "None", value: "" },
    { label: "% (Percentage)", value: "%" },
    { label: "h (Hours)", value: "h" },
    { label: "°C (Celsius)", value: "°C" },
    { label: "bar (Pressure)", value: "bar" },
    { label: "PSI (Pressure)", value: "PSI" },
    { label: "V (Volts)", value: "V" },
    { label: "ok", value: "ok" },
    { label: "clean", value: "clean" },
    { label: "no", value: "no" },
    { label: "yes", value: "yes" },
    { label: "Auto", value: "Auto" },
    { label: "manual", value: "manual" },
    { label: "off", value: "off" },
    { label: "fraction (Tank Level)", value: "fraction" },
    { label: "units (Meter Reading)", value: "units" },
  ];

  useEffect(() => {
    getItem("APPUSER").then((userData) => {
      if (typeof userData === "string") userData = JSON.parse(userData);
      setCurrentUser(userData?.user || null);
    });
  }, []);

  useEffect(() => {
    if (selectedFacilityId) {
      fetchChecklists();
    }
  }, [selectedFacilityId]);

  const fetchChecklists = async () => {
    try {
      setLoading(true);
      const response = await makeRequest2(
        `/api/daily_checklist/${selectedFacilityId}`,
        "GET",
      );

      if (
        response &&
        response.success &&
        response.data &&
        response.data.checklists
      ) {
        setChecklists(response.data.checklists);
      } else if (response && response.checklists) {
        setChecklists(response.checklists);
      } else if (response && Array.isArray(response)) {
        setChecklists(response);
      } else {
        setChecklists([]);
      }
    } catch (error) {
      console.error("Error fetching checklists:", error);
      toastify("Error fetching daily checklists", "error");
      setChecklists([]);
    } finally {
      setLoading(false);
    }
  };

  const createParent = async () => {
    try {
      setLoading(true);
      const response = await makeRequest2(
        `/api/daily_checklist/${selectedFacilityId}/parent`,
        "POST",
        {
          parentName: parentForm.parentName.trim(),
          checklistDate: parentForm.checklistDate,
        },
      );

      if (response && response.success) {
        const message =
          response.message ||
          response.data?.message ||
          "Parent created successfully";
        toastify(message, "success");
        fetchChecklists();
        setShowCreateParentDialog(false);
        resetParentForm();
      } else if (response && response.message) {
        toastify(response.message, "success");
        fetchChecklists();
        setShowCreateParentDialog(false);
        resetParentForm();
      } else {
        toastify("Failed to create parent checklist", "error");
      }
    } catch (error) {
      console.error("Error creating parent:", error);
      if (error.response) {
        const errorMessage =
          error.response.data?.error ||
          error.response.data?.message ||
          "Unknown error";
        toastify(`Error: ${errorMessage}`, "error");
      } else if (error.message) {
        toastify(`Error: ${error.message}`, "error");
      } else {
        toastify("Network error creating parent checklist", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const addReading = async () => {
    if (!selectedChecklist) {
      toastify("Please select a checklist first", "error");
      return;
    }

    try {
      setLoading(true);
      const response = await makeRequest2(
        `/api/daily_checklist/${selectedFacilityId}/checklist/${selectedChecklist._id}/reading`,
        "POST",
        {
          parentName: readingForm.parentName.trim(),
          childName: readingForm.childName.trim(),
          value: readingForm.value,
          date: readingForm.date,
          unit: readingForm.unit || undefined,
          notes: readingForm.notes.trim() || undefined,
          recordedBy: currentUser?.userId || "Unknown",
        },
      );

      if (response && response.success) {
        const message =
          response.message ||
          response.data?.message ||
          "Reading added successfully";
        toastify(message, "success");
        fetchChecklists();
        setShowAddReadingDialog(false);
        resetReadingForm();
      } else if (response && response.message) {
        toastify(response.message, "success");
        fetchChecklists();
        setShowAddReadingDialog(false);
        resetReadingForm();
      } else {
        toastify("Failed to add reading", "error");
      }
    } catch (error) {
      console.error("Error adding reading:", error);
      if (error.response) {
        const errorMessage =
          error.response.data?.error ||
          error.response.data?.message ||
          "Unknown error";
        toastify(`Error: ${errorMessage}`, "error");
      } else if (error.message) {
        toastify(`Error: ${error.message}`, "error");
      } else {
        toastify("Network error adding reading", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchReadingHistory = async (checklistId, parentName, childName) => {
    try {
      setLoading(true);
      const response = await makeRequest2(
        `/api/daily_checklist/${selectedFacilityId}/checklist/${checklistId}/parent/${parentName}/child/${childName}/history?limit=20`,
        "GET",
      );

      if (
        response &&
        response.success &&
        response.data &&
        response.data.readings
      ) {
        setReadingHistory(response.data.readings);
      } else if (response && response.readings) {
        setReadingHistory(response.readings);
      } else if (response && Array.isArray(response)) {
        setReadingHistory(response);
      } else {
        setReadingHistory([]);
        toastify("No reading history found", "info");
      }
    } catch (error) {
      console.error("Error fetching reading history:", error);
      toastify("Error fetching reading history", "error");
      setReadingHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const resetParentForm = () => {
    setParentForm({
      parentName: "",
      checklistDate: new Date(),
    });
  };

  const resetReadingForm = () => {
    setReadingForm({
      parentName: "",
      childName: "",
      value: "",
      date: new Date(),
      unit: "",
      notes: "",
      recordedBy: currentUser?.userId || "Unknown",
    });
  };

  const handleCreateParent = () => {
    resetParentForm();
    setShowCreateParentDialog(true);
  };

  const handleAddReading = (checklist) => {
    setSelectedChecklist(checklist);
    resetReadingForm();
    const parents = Object.keys(checklist.data || {});
    if (parents.length === 1) {
      setReadingForm((prev) => ({
        ...prev,
        parentName: parents[0],
        date: new Date(),
        recordedBy: currentUser?.userId || "Unknown",
      }));
    }
    setShowAddReadingDialog(true);
  };

  const handleViewHistory = async (checklist, parentName, childName) => {
    setSelectedChecklist(checklist);
    setSelectedParent(parentName);
    setSelectedChild(childName);
    await fetchReadingHistory(checklist._id, parentName, childName);
    setShowHistoryDialog(true);
  };

  const actionTemplate = (rowData) => (
    <div className="flex gap-2">
      <Button
        icon="pi pi-plus"
        className="p-button-rounded p-button-success p-button-sm hover:scale-105 transition-transform"
        onClick={() => handleAddReading(rowData)}
        tooltip="Add Reading"
      />
    </div>
  );

  const checklistExpansionTemplate = (data) => {
    const parents = Object.keys(data.data || {});

    return (
      <div className="p-4 bg-gray-50 rounded-lg animate-fade-in">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-800">
            Systems ({parents.length})
          </h3>
          <Button
            icon="pi pi-plus"
            label="Add Reading"
            className="p-button-sm p-button-success hover:scale-105 transition-transform"
            onClick={() => handleAddReading(data)}
          />
        </div>

        {parents.length === 0 ? (
          <div className="text-center py-6 bg-white rounded-lg shadow-sm">
            <i className="pi pi-exclamation-circle text-4xl text-gray-300"></i>
            <p className="mt-3 text-gray-600">
              No systems found in this checklist
            </p>
            <Button
              icon="pi pi-plus"
              label="Add First Reading"
              className="p-button-sm p-button-primary mt-3 hover:scale-105 transition-transform"
              onClick={() => handleAddReading(data)}
            />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {parents.map((parentName) => {
              const children = data.data[parentName] || [];
              return (
                <Card
                  key={parentName}
                  title={
                    <div className="flex justify-between items-center">
                      <span className="text-xl font-medium text-gray-900">
                        {parentName}
                      </span>
                      <Badge
                        value={children.length}
                        severity={children.length > 0 ? "info" : "secondary"}
                      />
                    </div>
                  }
                  className="shadow-sm hover:shadow-md transition-shadow"
                >
                  {children.length === 0 ? (
                    <div className="text-center py-4">
                      <p className="text-gray-600 mb-3">
                        No properties recorded
                      </p>
                      <Button
                        icon="pi pi-plus"
                        label="Add Property"
                        className="p-button-sm p-button-outlined hover:scale-105 transition-transform"
                        onClick={() => {
                          setSelectedChecklist(data);
                          setReadingForm((prev) => ({
                            ...prev,
                            parentName,
                            date: new Date(),
                            recordedBy: currentUser?.userId || "Unknown",
                          }));
                          setShowAddReadingDialog(true);
                        }}
                      />
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm text-gray-700">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-4 py-2 text-left">Property</th>
                            <th className="px-4 py-2 text-left">
                              Latest Value
                            </th>
                            <th className="px-4 py-2 text-left">
                              Last Updated
                            </th>
                            <th className="px-4 py-2 text-left">Readings</th>
                            <th className="px-4 py-2 text-left">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {children.map((child) => (
                            <tr
                              key={child._id || child.name}
                              className="border-b hover:bg-gray-50"
                            >
                              <td className="px-4 py-3">
                                <strong>{child.name}</strong>
                                {child.unit && (
                                  <span className="block text-xs text-gray-500">
                                    ({child.unit})
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                <span className="inline-block px-2 py-1 bg-blue-100 text-blue-800 rounded">
                                  {child.currentValue || "No value"}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <span className="text-xs">
                                  {child.currentDate
                                    ? new Date(
                                        child.currentDate,
                                      ).toLocaleDateString()
                                    : "Never"}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <Badge
                                  value={
                                    child.readings
                                      ? child.readings.filter((r) => r.isActive)
                                          .length
                                      : 0
                                  }
                                  severity="info"
                                />
                              </td>
                              <td className="px-4 py-3">
                                <Button
                                  icon="pi pi-history"
                                  className="p-button-rounded p-button-outlined p-button-sm hover:scale-105 transition-transform"
                                  onClick={() =>
                                    handleViewHistory(
                                      data,
                                      parentName,
                                      child.name,
                                    )
                                  }
                                  tooltip="View History"
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const filteredChecklists = checklists.filter((checklist) =>
    Object.values(checklist).some((value) =>
      value?.toString().toLowerCase().includes(searchKeyword.toLowerCase()),
    ),
  );

  const renderHeader = () => (
    <div className="flex justify-between items-center flex-wrap gap-3">
      <InputText
        placeholder="Search checklists..."
        value={searchKeyword}
        onChange={(e) => setSearchKeyword(e.target.value)}
        className="w-full md:w-64 rounded-lg border-gray-300"
      />
      <div className="flex gap-2">
        <Button
          label="Create System"
          icon="pi pi-plus"
          onClick={handleCreateParent}
          className="p-button-primary hover:scale-105 transition-transform"
        />
        <Button
          label="Refresh"
          icon="pi pi-refresh"
          onClick={fetchChecklists}
          className="p-button-outlined hover:scale-105 transition-transform"
        />
      </div>
    </div>
  );

  return (
    <div className="container mx-auto p-4">
      <Toast ref={toast} />

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <DataTable
          value={filteredChecklists}
          emptyMessage="No daily checklists found. Create your first system checklist to get started."
          sortMode="multiple"
          paginator
          rows={10}
          stripedRows
          loading={loading}
          header={renderHeader()}
          className="rounded-lg"
          expandedRows={expandedRows}
          onRowToggle={(e) => setExpandedRows(e.data)}
          rowExpansionTemplate={checklistExpansionTemplate}
        >
          <Column expander style={{ width: "3em" }} />
          <Column
            header="System Name"
            body={(rowData) => {
              const parents = Object.keys(rowData.data || {});
              return parents.length > 0 ? parents.join(", ") : "No systems";
            }}
            sortable
          />
          <Column
            field="createdAt"
            header="Created Date"
            body={(rowData) => new Date(rowData.createdAt).toLocaleDateString()}
            sortable
          />
          <Column
            field="childrenCount"
            header="Properties"
            body={(rowData) => {
              const totalChildren = Object.values(rowData.data || {}).reduce(
                (total, children) =>
                  total + (Array.isArray(children) ? children.length : 0),
                0,
              );
              return (
                <Badge
                  value={totalChildren}
                  severity={totalChildren > 0 ? "info" : "secondary"}
                />
              );
            }}
            sortable
          />
          <Column
            field="updatedAt"
            header="Last Updated"
            body={(rowData) => new Date(rowData.updatedAt).toLocaleDateString()}
            sortable
          />
          <Column body={actionTemplate} header="Actions" />
        </DataTable>
      </div>

      <Dialog
        header="Create New System Checklist"
        visible={showCreateParentDialog}
        onHide={() => setShowCreateParentDialog(false)}
        style={{ width: "90%", maxWidth: "500px" }}
        modal
        className="p-fluid"
        footer={
          <div className="d-flex justify-content-end gap-2">
            <Button
              label="Cancel"
              icon="pi pi-times"
              onClick={() => setShowCreateParentDialog(false)}
              className="p-button-outlined p-button-secondary"
            />
            <Button
              label="Create System"
              icon="pi pi-check"
              onClick={createParent}
              loading={loading}
              disabled={!parentForm.parentName.trim()}
              className="p-button-primary"
            />
          </div>
        }
      >
        <div className="row">
          <div className="col-12 mb-3">
            <div className="form-group">
              <label className="form-label">System Name *</label>
              <InputText
                id="parentName"
                value={parentForm.parentName}
                onChange={(e) =>
                  setParentForm({ ...parentForm, parentName: e.target.value })
                }
                placeholder="e.g., B1_GENERATOR, PUMP_ROOM, HVAC_SYSTEM"
                required
              />
              <small className="text-muted mt-1 d-block">
                System names must be unique across all checklists in this
                facility.
              </small>
            </div>
          </div>
          <div className="col-12 mb-3">
            <div className="form-group">
              <label className="form-label">Checklist Date</label>
              <Calendar
                id="checklistDate"
                value={parentForm.checklistDate}
                onChange={(e) =>
                  setParentForm({ ...parentForm, checklistDate: e.value })
                }
                showIcon
              />
            </div>
          </div>
        </div>
      </Dialog>

      <Dialog
        header="Add New Reading"
        visible={showAddReadingDialog}
        onHide={() => setShowAddReadingDialog(false)}
        style={{ width: "90%", maxWidth: "600px" }}
        modal
        className="p-fluid"
        footer={
          <div className="d-flex justify-content-end gap-2">
            <Button
              label="Cancel"
              icon="pi pi-times"
              onClick={() => setShowAddReadingDialog(false)}
              className="p-button-outlined p-button-secondary"
            />
            <Button
              label="Add Reading"
              icon="pi pi-check"
              onClick={addReading}
              loading={loading}
              disabled={
                !readingForm.parentName.trim() ||
                !readingForm.childName.trim() ||
                !readingForm.value
              }
              className="p-button-primary"
            />
          </div>
        }
      >
        <div className="row">
          <div className="col-12 mb-3">
            <div className="form-group">
              <label className="form-label">System Name *</label>
              <InputText
                id="parentName"
                value={readingForm.parentName}
                onChange={(e) =>
                  setReadingForm({ ...readingForm, parentName: e.target.value })
                }
                placeholder="e.g., B1_GENERATOR"
                required
              />
            </div>
          </div>
          <div className="col-12 mb-3">
            <div className="form-group">
              <label className="form-label">Property Name *</label>
              <InputText
                id="childName"
                value={readingForm.childName}
                onChange={(e) =>
                  setReadingForm({ ...readingForm, childName: e.target.value })
                }
                placeholder="e.g., fuel_level, temperature"
                required
              />
            </div>
          </div>
          <div className="col-12 mb-3">
            <div className="form-group">
              <label className="form-label">Value *</label>
              <InputText
                id="value"
                value={readingForm.value}
                onChange={(e) =>
                  setReadingForm({ ...readingForm, value: e.target.value })
                }
                placeholder="e.g., 45%, 27.4, ok"
                required
              />
            </div>
          </div>
          <div className="col-12 mb-3">
            <div className="form-group">
              <label className="form-label">Unit</label>
              <Dropdown
                id="unit"
                value={readingForm.unit}
                options={unitOptions}
                onChange={(e) =>
                  setReadingForm({ ...readingForm, unit: e.value })
                }
                placeholder="Select a unit"
                filter
                filterBy="label"
                showClear
              />
            </div>
          </div>
          <div className="col-12 mb-3">
            <div className="form-group">
              <label className="form-label">Reading Date & Time</label>
              <Calendar
                id="date"
                value={readingForm.date}
                onChange={(e) =>
                  setReadingForm({ ...readingForm, date: e.value })
                }
                showIcon
                showTime
              />
            </div>
          </div>
          <div className="col-12 mb-3">
            <div className="form-group">
              <label className="form-label">Notes</label>
              <InputTextarea
                id="notes"
                value={readingForm.notes}
                onChange={(e) =>
                  setReadingForm({ ...readingForm, notes: e.target.value })
                }
                rows={3}
                placeholder="Optional notes about this reading..."
              />
            </div>
          </div>
        </div>
      </Dialog>

      <Dialog
        header={`Reading History: ${selectedParent} - ${selectedChild}`}
        visible={showHistoryDialog}
        onHide={() => setShowHistoryDialog(false)}
        style={{ width: "90%", maxWidth: "900px" }}
        maximizable
        modal
        className="p-fluid"
      >
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <h6 className="text-lg font-semibold text-gray-800">
                Property: <strong>{selectedChild}</strong>
              </h6>
              <span className="text-sm text-gray-500">
                System: {selectedParent}
              </span>
            </div>
            <div className="text-right">
              <p className="mb-1">
                Total Readings:{" "}
                <Badge value={readingHistory.length} severity="info" />
              </p>
              <span className="text-sm text-gray-500">
                Active readings only
              </span>
            </div>
          </div>
        </div>

        <DataTable
          value={readingHistory}
          emptyMessage="No reading history found."
          sortMode="multiple"
          paginator
          rows={10}
          loading={loading}
          stripedRows
          className="rounded-lg"
        >
          <Column
            field="value"
            header="Value"
            sortable
            body={(rowData) => (
              <span className="inline-block px-2 py-1 bg-blue-100 text-blue-800 rounded">
                {rowData.value}
              </span>
            )}
          />
          <Column
            field="date"
            header="Date & Time"
            body={(rowData) => new Date(rowData.date).toLocaleString()}
            sortable
          />
          <Column
            field="recordedBy"
            header="Recorded By"
            body={(rowData) => rowData.recordedBy || "Unknown"}
            sortable
          />
          <Column
            field="notes"
            header="Notes"
            body={(rowData) => (
              <span title={rowData.notes}>
                {rowData.notes ? (
                  rowData.notes.length > 30 ? (
                    `${rowData.notes.substring(0, 30)}...`
                  ) : (
                    rowData.notes
                  )
                ) : (
                  <em className="text-gray-500">No notes</em>
                )}
              </span>
            )}
          />
          <Column
            field="isActive"
            header="Status"
            body={(rowData) => (
              <Badge
                value={rowData.isActive ? "Active" : "Deleted"}
                severity={rowData.isActive ? "success" : "danger"}
              />
            )}
            sortable
          />
          <Column
            field="createdAt"
            header="Recorded At"
            body={(rowData) =>
              rowData.createdAt
                ? new Date(rowData.createdAt).toLocaleString()
                : "N/A"
            }
            sortable
          />
        </DataTable>

        {readingHistory.length > 0 && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <div className="row text-center">
              <div className="col-md-3 mb-3">
                <h6 className="text-blue-600 font-medium">Latest Value</h6>
                <p className="font-semibold">{readingHistory[0]?.value}</p>
              </div>
              <div className="col-md-3 mb-3">
                <h6 className="text-green-600 font-medium">First Recorded</h6>
                <p>
                  {readingHistory[readingHistory.length - 1]?.date
                    ? new Date(
                        readingHistory[readingHistory.length - 1].date,
                      ).toLocaleDateString()
                    : "N/A"}
                </p>
              </div>
              <div className="col-md-3 mb-3">
                <h6 className="text-blue-600 font-medium">Total Records</h6>
                <p className="font-semibold">{readingHistory.length}</p>
              </div>
              <div className="col-md-3 mb-3">
                <h6 className="text-yellow-600 font-medium">Active Records</h6>
                <p className="font-semibold">
                  {readingHistory.filter((r) => r.isActive).length}
                </p>
              </div>
            </div>
          </div>
        )}
      </Dialog>

      {checklists.length === 0 && !loading && (
        <div className="bg-white rounded-lg shadow-md p-6 text-center">
          <i className="pi pi-check-circle text-6xl text-gray-300"></i>
          <h2 className="mt-4 text-2xl font-semibold text-gray-800">
            Welcome to Daily Checklist Management
          </h2>
          <p className="mt-2 text-gray-600 mb-6">
            Create system checklists to track daily readings and monitor
            facility equipment. Perfect for generators, pumps, HVAC systems, and
            more.
          </p>
          <div className="row">
            <div className="col-md-4 p-4">
              <i className="pi pi-plus-circle text-4xl text-blue-500"></i>
              <h3 className="mt-2 text-lg font-medium text-gray-800">
                1. Create System
              </h3>
              <p className="text-gray-500 text-sm">
                Start by creating a system like "B1_GENERATOR"
              </p>
            </div>
            <div className="col-md-4 p-4">
              <i className="pi pi-pencil text-4xl text-green-500"></i>
              <h3 className="mt-2 text-lg font-medium text-gray-800">
                2. Add Readings
              </h3>
              <p className="text-gray-500 text-sm">
                Record property values like fuel level, temperature
              </p>
            </div>
            <div className="col-md-4 p-4">
              <i className="pi pi-chart-line text-4xl text-blue-500"></i>
              <h3 className="mt-2 text-lg font-medium text-gray-800">
                3. Track History
              </h3>
              <p className="text-gray-500 text-sm">
                Monitor trends and changes over time
              </p>
            </div>
          </div>
          <Button
            label="Create Your First System"
            icon="pi pi-plus"
            onClick={handleCreateParent}
            className="p-button-primary p-button-lg mt-6 hover:scale-105 transition-transform"
          />
        </div>
      )}
    </div>
  );
};

export default DailyChecklistTab;
